import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { CachedParams } from '../models/interface/cachedParams.interface';

/**
 * This class represent a value into the cache store
 */
class CacheValue<T> {
  /**
   * Create a cache value
   * @param  {T} cacheValue? value
   * @param  {} invalidate=0 timestamp specifying the duration of the value
   */
  constructor(private cacheValue?: T, private invalidate = 0) {
    /** */
  }

  /**
   * check if the value is valid
   * @returns true if a value stored into the store is valid, the invalidation is built using the timespan
   */
  get valid() {
    if (this.cacheValue === undefined || this.cacheValue === null) {
      return false;
    }
    const ts = new Date().getTime();
    if (this.invalidate < ts) {
      return false;
    }
    return true;
  }

  /**
   * get the value into the store
   * @returns The value stored into the cache
   */
  get value() {
    return this.cacheValue;
  }

  /**
   * create a json object with the value and the invalidate timestamp
   * @returns a json object
   */
  get serialize() {
    return {
      value: this.cacheValue,
      invalidate: this.invalidate,
    };
  }

  /**
   * update the value into the store
   * @param  {T} val the new value
   * @param  {number} timeSpan the timespan to invalidate the value
   * @returns void
   */
  updateValue(val: T, timeSpan: number): void {
    this.cacheValue = val;
    this.invalidate = new Date().getTime() + timeSpan;
  }
}

/**
 * Cache specification into the store
 */
class CacheItem<T> {
  /**
   * Crea a new item into the store
   * @param  {CacheStore} store where the cache values are stored
   * @param  {number} timeSpan duration to invalidate a value
   * @param  {(...values:any[])=>string} keyGenerator function to generate the value key
   * @param  {(...values:any[])=>Promise<T>} generator function to calculate the value when it is not stored
   */
  constructor(
    private store: CacheStore,
    private timeSpan: number,
    private keyGenerator: (...values: any[]) => string,
    private generator: (...values: any[]) => Promise<T>
  ) {
    /** */
  }

  /**
   * calculate the cache's value
   * @param  {any[]} ...values
   */
  async value(...values: any[]) {
    const key = this.keyGenerator(...values);
    let value = this.store.get(key) as CacheValue<T>;
    if (!value) {
      value = new CacheValue<T>();
    }
    if (value.valid) {
      return value.value;
    }
    const calculatedValue = await this.generator(...values);
    value.updateValue(calculatedValue, this.timeSpan);
    this.store.put(key, value);
    return calculatedValue;
  }
}

/**
 * Define how the values are store into the cache
 */
abstract class CacheStore {
  /**
   * Store a value into the store
   * @param  {string} key  the key to store
   * @param  {CacheValue<any>} value the value
   * @returns void
   */
  abstract put(key: string, value: CacheValue<any>): void;

  /**
   * find a value into to the store
   * @param  {string} key the name how the value was stored
   * @returns CacheValue a value stored or null if not exists
   */
  abstract get(key: string): CacheValue<any>;

  /**
   * clear values stored in cache with a prefix key
   * @param  {string} prefix the prefix key how the value was stored
   */
  abstract clear(key?: string): void;
}

/**
 * Store values in memory
 */
class MapStore extends CacheStore {
  /**
   * the underlying store
   */
  private values = new Map<string, CacheValue<any>>();

  /**
   * @inheritdoc
   * @param  {string} key
   * @param  {CacheValue<any>} value
   * @returns void
   */
  put(key: string, value: CacheValue<any>): void {
    this.values.set(key, value);
  }

  /**
   * @inheritdoc
   * @param  {string} key
   * @returns CacheValue
   */
  get(key: string): CacheValue<any> {
    return this.values.get(key);
  }

  /**
   * @inheritdoc
   * @param  {string} key
   * @returns void
   */
  clear(keyPrefix?: string): void {
    if (!keyPrefix) {
      this.values.clear();
      return environment.guardClause;
    }
    const keysToRemove = [];
    for (const [key, _] of this.values) {
      if (key.startsWith(keyPrefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      this.values.delete(key);
    });
  }
}

/**
 * Store values in localStorage
 */
class LocalStorageStore extends CacheStore {
  /**
   * @inheritdoc
   * @param  {string} key
   * @param  {CacheValue<any>} value
   * @returns void
   */
  put(key: string, value: CacheValue<any>): void {
    localStorage.setItem(`@cache:${key}`, JSON.stringify(value.serialize));
  }

  /**
   * @inheritdoc
   * @param  {string} key
   * @returns CacheValue
   */
  get(key: string): CacheValue<any> {
    const item = localStorage.getItem(`@cache:${key}`);
    if (!item) {
      return null;
    }
    const { value, invalidate } = JSON.parse(item);
    return new CacheValue<any>(value, invalidate);
  }

  /**
   * @inheritdoc
   * @param  {string} key
   * @returns void
   */
  clear(keyPrefix: string = ''): void {
    const testKeyPrefix = `@cache:${keyPrefix}`;
    const len = localStorage.length;
    const keysToDelete = [];
    for (let i = 0; i < len; i++) {
      const key = localStorage.key(i);
      if (key.startsWith(testKeyPrefix)) {
        keysToDelete.push(key);
      }
    }
    for (const key of keysToDelete) {
      localStorage.removeItem(key);
    }
  }
}

/**
 * default memory store
 */
const memoryStore = new MapStore();
/**
 * default localStorage store
 */
const localStorageStore = new LocalStorageStore();

export enum CacheStoreType {
  MEMORY = 'memory',
  LOCAL_STORAGE = 'localStorage',
}

/**
 * default keyGenerator, convert the arguments into a string
 */
export const stringKeyGenerator = (...values: any[]) => {
  return values.map(itm => String(itm)).join(',');
};

export enum TimeUnit {
  SECOND = 1000,
  MINUTE = SECOND * 60,
  HOUR = MINUTE * 60,
}

/**
 * Type to define a cache generator
 */
export type Cache<T> = (...values: any[]) => Promise<T>;

@Injectable({
  providedIn: 'root',
})
export class CacheService {
  /**
   * calculate a timespan
   * @param  {number} value value of time
   * @param  {TimeUnit} unit unit factor
   */
  timespan(value: number, unit: TimeUnit) {
    return value * unit;
  }
  /**
   * crear a function that find in the store for values
   * @param  {any} bindTo The service o class who owns the generator
   * @param  {CachedParams<T>} definition definition of the cache
   * @returns Cache a function to find values
   */
  cache<T>(
    bindTo: any,
    {
      generator,
      keyPrefix,
      store = CacheStoreType.MEMORY,
      timeSpan = this.timespan(1, TimeUnit.HOUR),
      keyGenerator = stringKeyGenerator,
    }: CachedParams<T>
  ): Cache<T> {
    const bindName = bindTo.constructor.name;
    const bindedFunc = generator.bind(bindTo) as (...values: any[]) => Promise<T>;
    const cache = new CacheItem<T>(
      this.getStore(store),
      timeSpan,
      (...values: any[]) => `${bindName}::${keyPrefix}::${keyGenerator(...values)}`,
      bindedFunc
    );
    return (...values: any[]) => cache.value(...values);
  }

  /**
   * return a cache storage solution
   * @param  {CacheStoreType} store
   * @returns CacheStore
   */
  private getStore(store: CacheStoreType): CacheStore {
    switch (store) {
      case CacheStoreType.LOCAL_STORAGE:
        return localStorageStore;
      case CacheStoreType.MEMORY:
      default:
        return memoryStore;
    }
  }

  /**
   * remove cache values who use a prefix key
   * @param  {new(...args:any[]} bindTo
   */
  clearCache(bindTo?: new (...args: any[]) => any, keyPrefix?: string) {
    if (!bindTo) {
      memoryStore.clear();
      localStorageStore.clear();
      return environment.guardClause;
    }
    const bindName = typeof bindTo === 'string' ? bindTo : bindTo.constructor.name;
    const key = keyPrefix ? `${bindName}::${keyPrefix}::` : `${bindName}::`;
    memoryStore.clear(key);
    localStorageStore.clear(key);
  }
}
